/**
 * ============================================================================
 * ACTIVITY TIMELINE COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for displaying user activity timeline
 */

import React, { useEffect } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';

/**
 * Activity Timeline Component
 * @param {Object} props
 * @param {string} props.period - Activity period (week, month, year, all)
 * @returns {JSX.Element}
 */
const ActivityTimeline = ({ period = 'month' }) => {
  const { loadUserActivityAnalytics, userActivityAnalytics, loading } = useAnalytics();
  const { showError } = useToast();
  const { formatDate, formatTime, language } = useI18n();

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadUserActivityAnalytics({ period });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement de l\'activité' : 'Failed to load activity')
        );
      }
    };

    loadData();
  }, [period, loadUserActivityAnalytics, showError, language]);

  if (loading && !userActivityAnalytics) {
    return (
      <Card variant="glass" className="p-12">
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  const activities = userActivityAnalytics?.data || userActivityAnalytics?.activities || [];

  const getActivityIcon = (type) => {
    const icons = {
      transaction_created: '📦',
      transaction_completed: '✅',
      payment_received: '💰',
      dispute_raised: '⚠️',
      rating_given: '⭐',
      profile_updated: '👤',
      wallet_deposit: '💵',
      wallet_withdrawal: '💸',
    };
    return icons[type] || '📊';
  };

  const getActivityColor = (type) => {
    const colors = {
      transaction_created: 'from-blue-500 to-cyan-500',
      transaction_completed: 'from-green-500 to-emerald-500',
      payment_received: 'from-yellow-500 to-orange-500',
      dispute_raised: 'from-red-500 to-pink-500',
      rating_given: 'from-purple-500 to-pink-500',
      profile_updated: 'from-gray-500 to-slate-500',
      wallet_deposit: 'from-green-500 to-teal-500',
      wallet_withdrawal: 'from-purple-500 to-indigo-500',
    };
    return colors[type] || 'from-gray-500 to-slate-500';
  };

  if (activities.length === 0) {
    return (
      <Card variant="glass" className="p-12">
        <EmptyState
          icon="📊"
          title={language === 'fr' ? 'Aucune activité' : 'No activity'}
          description={
            language === 'fr'
              ? 'Vous n\'avez pas encore d\'activité récente'
              : "You don't have any recent activity yet"
          }
        />
      </Card>
    );
  }

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <h3 className="text-xl font-black text-gray-900 dark:text-white mb-6">
        📈 {language === 'fr' ? 'Chronologie de l\'Activité' : 'Activity Timeline'}
      </h3>
      <div className="space-y-4">
        {activities.map((activity, index) => (
          <div
            key={activity.id || index}
            className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors animate-fadeInUp"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getActivityColor(activity.type)} flex items-center justify-center text-2xl flex-shrink-0 shadow-lg`}>
              {getActivityIcon(activity.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                {activity.title || activity.description || activity.type}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                {activity.createdAt && (
                  <>
                    <span>{formatDate(activity.createdAt)}</span>
                    <span>•</span>
                    <span>{formatTime(activity.createdAt)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default ActivityTimeline;
